'use client';

import React, { useEffect, useState, useCallback } from 'react';

interface ChargebeeUpdateButtonProps {
  className?: string;
}

declare global {
  interface Window {
    Chargebee?: {
      init: (config: { site: string }) => any;
      getInstance: () => any;
    };
    cbInstance?: any;
  }
}

export function ChargebeeUpdateButton({ className }: ChargebeeUpdateButtonProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    // Set mounted to true to ensure we only render on client
    setIsMounted(true);

    // Load Chargebee script only once on client side
    if (typeof window !== 'undefined' && !document.querySelector('script[src*="chargebee.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://js.chargebee.com/v2/chargebee.js';
      script.setAttribute('data-cb-site', 'leadpops');
      script.async = true;

      // Initialize Chargebee once the script loads
      script.onload = () => {
        if (window.Chargebee) {
          window.cbInstance = window.Chargebee.init({
            site: 'leadpops'
          });
          setIsScriptLoaded(true);
        }
      };

      document.head.appendChild(script);
    } else if (window.Chargebee && !window.cbInstance) {
      // Script already loaded but not initialized
      window.cbInstance = window.Chargebee.init({
        site: 'leadpops'
      });
      setIsScriptLoaded(true);
    } else if (window.cbInstance) {
      // Already initialized
      setIsScriptLoaded(true);
    }
  }, []);

  // Handle click manually using Chargebee's programmatic API
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Make sure we have a Chargebee instance
    if (!window.cbInstance && window.Chargebee) {
      window.cbInstance = window.Chargebee.init({
        site: 'leadpops'
      });
    }

    // Open the portal using the programmatic method
    if (window.cbInstance) {
      // Create the portal session programmatically
      window.cbInstance.createChargebeePortal().open({
        close: () => {
          // Portal closed callback - we can re-use the same instance
          console.log('Chargebee portal closed');
        }
      });
    } else {
      console.error('Chargebee instance not available');
    }
  }, []);

  // Don't render anything on server to avoid hydration mismatch
  if (!isMounted) {
    return null;
  }

  // Button with manual click handling - no data-cb-type since we're handling it manually
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isScriptLoaded}
      className={`inline-flex items-center justify-center gap-2 rounded-lg bg-leadpops-pink px-8 py-4 text-lg font-medium text-white transition-colors hover:bg-leadpops-pink/90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${className || ''}`}
    >
      Continue
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
      </svg>
    </button>
  );
}